import React, { useEffect, lazy, Suspense } from 'react';
import { AlertCircle } from 'lucide-react';
import { useParams, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { usePricingSettings, PricingSettingsProvider } from '../../../contexts/PricingSettingsContext';
import ErrorBoundary from '../../../components/ErrorBoundary';
import TabLoader from '../../../components/admin/TabLoader';
import SettingsHeader from '../../../components/admin/SettingsHeader';
import PricingTabs from '../../../components/admin/PricingTabs';
import ConfirmModalProvider from '../../../components/admin/ConfirmModal';

// Lazy-loaded components with preload capability
const RegistrationPricingTab = lazy(() => {
  const mod = import('../../../components/admin/RegistrationPricingTab');
  mod.then(m => m.default?.preload?.());
  return mod;
});
const CompetitionPricingTab = lazy(() => {
  const mod = import('../../../components/admin/CompetitionPricingTab');
  mod.then(m => m.default?.preload?.());
  return mod;
});

const PricingSettingsContent = () => {
  const {
    state,
    saveSettings,
    resetSettings,
    handleSubmit,
    initialize,
  } = usePricingSettings();

  const { isLoading, notification, hasUnsavedChanges } = state;

  const { tab = 'registration' } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        await initialize();
      } catch (err) {
        console.error('Failed to initialize pricing settings:', err);
      }
    })();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const tabErrorFallback = (error) => (
    <div className="p-6 border border-error/20 bg-error/5 rounded-lg flex items-start gap-4" role="alert">
      <div className="text-error mt-1">
        <AlertCircle size={24} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-error mb-2">Tab Content Error</h3>
        <p className="text-base-content/80">
          There was an error loading the <strong>{tab}</strong> tab. Try switching tabs or refreshing the page.
        </p>
        {error && (
          <pre className="mt-4 p-3 bg-base-300 rounded text-sm overflow-auto max-h-48">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );

  const handleSave = async () => {
    try {
      await handleSubmit(saveSettings)();
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all unsaved changes?')) {
      resetSettings();
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      <SettingsHeader
        title="Pricing Settings"
        description="Manage registration and competition entry pricing, late fees, and discount rates"
        onSave={handleSave}
        onReset={handleReset}
        isLoading={isLoading}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {notification && (
        <div role="alert" className={`alert ${notification.type === 'error' ? 'alert-error' : 'alert-success'} flex items-center`}>
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{notification.message}</span>
        </div>
      )}

      <PricingTabs activeTab={tab} onTabChange={(newTab) => navigate(`/admin/settings/pricing/${newTab}`)} />

      <div className="grid grid-cols-1 gap-6">
        <ErrorBoundary fallback={tabErrorFallback}>
          <Suspense fallback={<TabLoader />}>
            <Routes>
              <Route path="registration" element={<RegistrationPricingTab />} />
              <Route path="competition" element={<CompetitionPricingTab />} />
              <Route path="*" element={<Navigate to="registration" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  );
};

const PricingSettings = () => (
  <PricingSettingsProvider>
    <ConfirmModalProvider>
      <PricingSettingsContent />
    </ConfirmModalProvider>
  </PricingSettingsProvider>
);

export default PricingSettings;
